import React, { useRef, useEffect } from "react";

// React wrapper for Om Design System DatePicker web component
const OmDatePicker = React.forwardRef(({ 
  name,
  label,
  min,
  max,
  required = false,
  value,
  onChange,
  ...props 
}, ref) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleInput = (event) => {
      if (onChange) {
        // Pass the original event object so parent components can access e.target.value
        onChange(event);
      }
    };

    const addEventListeners = () => {
      try {
        // Check if element is still connected before adding listeners
        if (!element || !element.isConnected) {
          return () => {};
        }
        
        // Listen for both input and change events from the web component
        element.addEventListener('input', handleInput);
        element.addEventListener('change', handleInput);
        
        return () => {
          if (element && element.isConnected) {
            try {
              element.removeEventListener('input', handleInput);
              element.removeEventListener('change', handleInput);
            } catch (error) {
              console.warn('Failed to remove event listeners from OmDatePicker:', error);
            }
          }
        };
      } catch (error) {
        console.warn('Failed to attach event listeners to OmDatePicker:', error);
        return () => {};
      }
    };

    // Check if component is ready or wait for it
    if (element.shadowRoot || element.isConnected) {
      return addEventListeners();
    } else {
      // Wait for component to be ready
      const timeout = setTimeout(addEventListeners, 100);
      return () => clearTimeout(timeout);
    }
  }, [onChange]);

  // Set value when it changes
  useEffect(() => {
    const element = elementRef.current;
    if (element && element.isConnected && value !== undefined && element.value !== value) {
      try {
        // Check if component internal state is valid before setting value
        element.value = value;
      } catch (error) {
        console.warn('Failed to set value on OmDatePicker:', error);
      }
    }
  }, [value]);

  return (
    <omk-date-picker
      ref={(el) => {
        elementRef.current = el;
        if (ref) {
          if (typeof ref === 'function') {
            ref(el);
          } else {
            ref.current = el;
          }
        }
      }}
      name={name}
      label={label}
      min={min}
      max={max}
      required={required}
      value={value}
      {...props}
    />
  );
});

OmDatePicker.displayName = 'OmDatePicker';

export default OmDatePicker;
